import caas from "@/services/caas";

const getInitialState = () => ({
    catalogList: [],
    categoryList: [],
    itemList: [],
    catalogItem: {},
    ui: {
        currentCatalog: {
            catalogId: 4,
        },
        currentCategory: {},
        currentCategoryFilter: {
            brands: [],
            attributes: []
        },
        rowsToBatch: 50,
        rowNum: 1,
        loadMore: false,
        locale: "",
        loading: false,
        searchText: "",
        queryType: "fetch"
    },
});

// STATE
const state = getInitialState();

// ACTIONS
const actions = {

    applyBrandFilter: (context, _brands) => {
        context.commit("setItemList", []);
        context.commit("setRowNum", 1);
        context.commit("setBrandFilter", _brands);
        context.dispatch("openQuery");
    },

    applyAttributeFilter: (context, pAttributeValueIds) => {
        context.commit("setItemList", []);
        context.commit("setRowNum", 1);
        const attributeFilter = pAttributeValueIds.map(id => ({ attributeValueId: id }));
        context.commit("setAttributeFilter", attributeFilter);
        context.dispatch("openQuery");
    },

    clearFilters: (context) => {
        context.commit("clearFilters");
        context.dispatch("openQuery");
    },

    openQuery: (context) => {
        let params = {};
        if (context.state.ui.queryType === "fetch") {
            params = {
                catalogId: state.ui.currentCatalog.catalogId,
                categoryId: state.ui.currentCategory?.categoryId || 0,
                categoryFilter: state.ui.currentCategoryFilter,
                languageCode: state.ui.locale,
                rowsToBatch: state.ui.rowsToBatch,
                rowNum: state.ui.rowNum,
            };
            context.dispatch("fetchCatalogItems", params);
        } else if (context.state.ui.queryType === "search") {
            params = {
                catalogId: state.ui.currentCatalog.catalogId,
                searchText: state.ui.searchText,
                languageCode: state.ui.locale,
                rowsToBatch: state.ui.rowsToBatch,
                rowNum: state.ui.rowNum,
            };
            context.dispatch("searchCatalogItems", params);
        }
    },

    fetchCatalogItems: (context, params) => {
        return caas
            .rpc("fetchCatalogItems", params)
            .then((response) => {
                if (response.data.success) {
                    context.commit(
                        "pushItemList",
                        response.data.success.data
                    );
                    context.commit(
                        "setRowNum",
                        response.data.success.rowNum
                    );
                    context.commit(
                        "setLoadMore",
                        response.data.success.rowCnt ===
                        state.ui.rowsToBatch
                    );
                }
                if (response.data.error) throw response.data.error;
            })
            .catch((error) => {
                console.error(error)
            })
            .finally(() => {
                context.commit("setLoading", false);

            });
    },

    fetchCatalogItem: (context, pItemId) => {
        context.commit("setLoading", true);
        context.commit("setCatalogItem", null);
        let params = {
            itemId: pItemId,
            languageCode: context.state.ui.locale
        }
        return caas
            .rpc("getCatalogItem", params)
            .then((response) => {
                context.commit("setLoading", false);
                if (response.data.success) {
                    context.commit(
                        "setCatalogItem",
                        response.data.success.data.catalogItem[0]
                    );
                }
            })
            .catch((e) => {
                context.commit("setLoading", false);
                console.log(e)
            });
    },

    fetchCatalog: (context) => {
        let params = {
            catalogId: context.state.ui.currentCatalog.catalogId,
            languageCode: context.state.ui.locale
        };
        // catalog
        return caas
            .rpc("fetchProductCatalog", params)
            .then((response) => {
                context.commit(
                    "SET_CATEGORY_LIST",
                    response.data.success.categories
                );
            })
            .catch((error) => {
                console.log(error.message);
            });
    },

    fetchCategory: (context, pCategoryId) => {
        context.commit("setLoading", true);
        context.commit("setItemList", []);

        let params = {
            catalogId: context.state.ui.currentCatalog.catalogId,
            categoryId: pCategoryId,
            languageCode: context.state.ui.locale
        }
        return caas.rpc("fetchProductCategory", params)
            .then((response) => {
                context.commit("setLoading", false);
                if (response.data.success) {
                    context.commit("setCurrentCategory", response.data.success);
                    context.dispatch("openQuery");
                }
            })
            .catch((error) => {
                console.log(error.message);
                context.commit("setLoading", false);
            });
    },

    performSearch: (context) => {
        context.commit("setQueryType", "search");
        context.commit("setItemList", []);
        context.commit("setRowNum", 1);
        context.dispatch("openQuery");
    },

    searchCatalogItems: (context, params) => {
        return caas
            .rpc("searchCatalogItems", params)
            .then((response) => {
                if (response.data.success) {
                    context.commit(
                        "pushItemList",
                        response.data.success.data
                    );
                    context.commit(
                        "setRowNum",
                        response.data.success.rowNum
                    );
                    context.commit(
                        "setLoadMore",
                        response.data.success.rowCnt ===
                        state.ui.rowsToBatch
                    );
                }
                if (response.data.error) throw response.data.error;
            })
            .catch((error) => {
                console.error(error)
            })
            .finally(() => {
                context.commit("setLoading", false);
            });
    },

    reset: (context) => {
        context.commit("RESET");
    },

    resetCategory: (context) => {
        context.commit("RESET_CATEGORY");
    },
};

// MUTATIONS
const mutations = {
    resetState(state) {
        Object.assign(state, getInitialState());
    },
    setBrandFilter: (state, payload) => {
        state.ui.currentCategoryFilter.brands = payload;
    },

    setAttributeFilter: (state, payload) => {
        state.ui.currentCategoryFilter.attributes = payload;
    },

    clearFilters: (state) => {
        state.ui.currentCategoryFilter = { brands: [], attributes: [] };
        state.ui.queryType = "fetch";
        // reset
        state.ui.rowNum = 1;
        state.ui.loadMore = false;
        state.itemList = [];
        state.catalogItem = {};
    },

    clearSearch: (state) => {
        state.ui.searchText = "";
        state.ui.queryType = "fetch";
        state.itemList = [];
        state.catalogItem = {};
        state.ui.rowNum = 1;
        state.ui.loadMore = false;
    },

    setLocale: (state, _locale) => {
        state.ui.locale = _locale
    },

    setCatalogItem: (state, payload) => {
        state.catalogItem = payload;
    },

    RESET_CATEGORY: (state) => {
        const initialState = getInitialState();
        state.itemList = initialState.itemList;
        state.catalogItem = initialState.catalogItem;
        state.ui.currentCategory = initialState.ui.currentCategory;
        state.ui.currentCategoryFilter = initialState.ui.currentCategoryFilter;
        state.ui.rowsToBatch = initialState.ui.rowsToBatch;
        state.ui.rowNum = initialState.ui.rowNum;
        state.ui.loadMore = initialState.ui.loadMore;
        state.ui.searchText = initialState.ui.searchText;
        state.ui.queryType = initialState.ui.queryType;

    },

    SET_CATEGORY_LIST: (state, payload) => {
        state.categoryList = payload.map(c => ({ ...c, menu: false }));
    },

    setCurrentCategory: (state, pCategory) => {
        if (pCategory?.filterOptions?.attributes) {
            pCategory.filterOptions.attributes.forEach(attr => {
                attr.selectedValues = [];
            });
        }
        state.ui.currentCategory = pCategory;
        // reset
        state.ui.currentCategoryFilter = { brands: [], attributes: [] };
        state.ui.rowNum = 1;
        state.ui.loadMore = false;
        state.itemList = [];
        state.catalogItem = {};
        state.ui.searchText = "";
        state.ui.queryType = "fetch";
    },

    setItemList: (state, payload) => {
        state.itemList = payload;
    },

    pushItemList: (state, payload) => {
        if (Array.isArray(payload)) {
            state.itemList.push(...payload);
        }
    },

    setLoading: (state, pLoading) => {
        state.ui.loading = pLoading;
    },

    setLoadMore: (state, payload) => {
        state.ui.loadMore = payload;
    },

    setRowNum: (state, payload) => {
        state.ui.rowNum = payload;
    },

    setSearchText: (state, payload) => {
        state.ui.searchText = payload;
    },

    setQueryType: (state, payload) => {
        state.ui.queryType = payload;
    },

    RESET: (state) => {
        const locale = state.ui.locale;
        Object.assign(state, getInitialState());
        state.ui.locale = locale;
    }
};

// GETTERS
const getters = {
    mainCategories: (state) => {
        const { currentCategory } = state.ui;
        if (currentCategory && currentCategory.categoryId) return [];

        var mainCategories = [];
        state.categoryList.forEach(category => {
            mainCategories.push({
                id: category.id,
                name: category.name,
                categoryId: category.id,

                imageThumbUrl: category.imageThumbUrl,
            });
        });
        return mainCategories;
    },
    itemList: (state) => {
        return state.itemList;
    },
    catalogItem: (state) => {
        return state.catalogItem;
    },
    catalogItemSKU: (state) => {
        if (state.catalogItem.itemSKUs) return state.catalogItem.itemSKUs[0];
    },
    currentCategory: (state) => {
        return state.ui.currentCategory;
    },
    currentCategoryName: (state) => {
        return state.ui.currentCategory?.categoryName || "";
    },
    loading: (state) => {
        return state.ui.loading;
    },
    loadMore: (state) => {
        return state.ui.loadMore;
    },
    categoryList: (state) => {
        return state.categoryList;
    },
    searchText: (state) => {
        return state.ui.searchText;
    },
    rowNum: (state) => {
        return state.ui.rowNum;
    },
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
};
